import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add,
  Download,
  CheckCircle,
  Publish,
  Visibility,
  Close,
  Article,
  Refresh,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet } from '../services/apiClient';
import { kidService, KIDDocument, KIDTemplate } from '../services/kidService';
import { LoggerService } from '../services/LoggerService';

interface Fund {
  id: string;
  name: string;
}

interface ShareClass {
  id: string;
  fund_id: string;
  name: string;
  isin: string;
  currency: string;
}

const STATUS_CHIP_MAP: Record<string, { color: 'default' | 'info' | 'primary' | 'success'; label: string }> = {
  draft: { color: 'default', label: 'Draft' },
  pending_review: { color: 'info', label: 'Pending Review' },
  approved: { color: 'primary', label: 'Approved' },
  published: { color: 'success', label: 'Published' },
};

const SRI_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444', '#dc2626', '#991b1b'];

const KIDManagementPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [selectedShareClassId, setSelectedShareClassId] = useState('');
  const [kidHistory, setKidHistory] = useState<KIDDocument[]>([]);
  const [templates, setTemplates] = useState<KIDTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedKid, setSelectedKid] = useState<KIDDocument | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadFunds = useCallback(async () => {
    try {
      const response = await apiGet<{ funds: Fund[] }>('/funds/');
      setFunds(response.funds || []);
    } catch (err: any) {
      LoggerService.error('Failed to load funds', err);
      setError(err.message || 'Failed to load funds');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await kidService.getTemplates();
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (err: any) {
      LoggerService.error('Failed to load KID templates', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadFunds(), loadTemplates()]);
      setLoading(false);
    };
    init();
  }, [loadFunds, loadTemplates]);

  useEffect(() => {
    if (!selectedFundId) {
      setShareClasses([]);
      setSelectedShareClassId('');
      return;
    }
    const loadShareClasses = async () => {
      try {
        const data = await apiGet<ShareClass[]>(`/share-classes/?fund_id=${selectedFundId}`);
        setShareClasses(data);
        setSelectedShareClassId('');
        setKidHistory([]);
      } catch (err: any) {
        LoggerService.error('Failed to load share classes', err);
        enqueueSnackbar('Failed to load share classes', { variant: 'error' });
      }
    };
    loadShareClasses();
  }, [selectedFundId, enqueueSnackbar]);

  useEffect(() => {
    if (!selectedShareClassId) {
      setKidHistory([]);
      return;
    }
    const loadHistory = async () => {
      try {
        const data = await kidService.getHistory(selectedShareClassId);
        setKidHistory(data);
      } catch (err: any) {
        LoggerService.error('Failed to load KID history', err);
        setKidHistory([]);
      }
    };
    loadHistory();
  }, [selectedShareClassId]);

  const handleGenerate = async () => {
    if (!selectedShareClassId || !selectedTemplateId) {
      enqueueSnackbar('Please select a share class and template', { variant: 'warning' });
      return;
    }
    setGenerating(true);
    try {
      const newKid = await kidService.generate(selectedShareClassId, selectedTemplateId);
      setKidHistory((prev) => [newKid, ...prev]);
      enqueueSnackbar('KID document generated successfully', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to generate KID', err);
      enqueueSnackbar(err.message || 'Failed to generate KID', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDetail = async (kid: KIDDocument) => {
    try {
      const detail = await kidService.getById(kid.id);
      setSelectedKid(detail);
      setDetailDialogOpen(true);
    } catch (err: any) {
      LoggerService.error('Failed to load KID detail', err);
      enqueueSnackbar(err.message || 'Failed to load KID details', { variant: 'error' });
    }
  };

  const handleApprove = async (kidId: string) => {
    setActionLoading(kidId);
    try {
      const updated = await kidService.approve(kidId);
      setKidHistory((prev) => prev.map((k) => (k.id === kidId ? updated : k)));
      if (selectedKid?.id === kidId) setSelectedKid(updated);
      enqueueSnackbar('KID approved successfully', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to approve KID', err);
      enqueueSnackbar(err.message || 'Failed to approve KID', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (kidId: string) => {
    setActionLoading(kidId);
    try {
      const updated = await kidService.publish(kidId);
      setKidHistory((prev) => prev.map((k) => (k.id === kidId ? updated : k)));
      if (selectedKid?.id === kidId) setSelectedKid(updated);
      enqueueSnackbar('KID published successfully', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to publish KID', err);
      enqueueSnackbar(err.message || 'Failed to publish KID', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (kidId: string) => {
    try {
      const blob = await kidService.download(kidId);
      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `KID-${kidId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('KID downloaded', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to download KID', err);
      enqueueSnackbar(err.message || 'Failed to download KID', { variant: 'error' });
    }
  };

  const renderSRIIndicator = (value: number) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {[1, 2, 3, 4, 5, 6, 7].map((level) => (
          <Box
            key={level}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              backgroundColor: level <= value ? SRI_COLORS[level - 1] : 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: level === value ? '2px solid #fff' : '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: level === value ? 700 : 400 }}>
              {level}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderPerformanceTable = (scenarios: any) => {
    if (!scenarios) return <Typography color="text.secondary">No performance data available</Typography>;
    const rows = [
      { label: 'Stress', data: scenarios.stress },
      { label: 'Unfavourable', data: scenarios.unfavourable },
      { label: 'Moderate', data: scenarios.moderate },
      { label: 'Favourable', data: scenarios.favourable },
    ];
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scenario</TableCell>
              <TableCell align="right">1 Year</TableCell>
              <TableCell align="right">3 Years</TableCell>
              <TableCell align="right">5 Years (RHP)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.data?.year_1 != null ? `${row.data.year_1}%` : '-'}</TableCell>
                <TableCell align="right">{row.data?.year_3 != null ? `${row.data.year_3}%` : '-'}</TableCell>
                <TableCell align="right">{row.data?.year_5 != null ? `${row.data.year_5}%` : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCostTable = (costs: any) => {
    if (!costs) return <Typography color="text.secondary">No cost data available</Typography>;
    const rows = [
      { label: 'Entry Costs', value: costs.entry_costs },
      { label: 'Exit Costs', value: costs.exit_costs },
      { label: 'Ongoing Costs', value: costs.ongoing_costs },
      { label: 'Performance Fee', value: costs.performance_fee },
      { label: 'Reduction in Yield (RIY)', value: costs.riy },
    ];
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">
                  {row.value != null ? (typeof row.value === 'number' ? `${row.value}%` : row.value) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={() => { setError(null); loadFunds(); }}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>KID Document Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Generate, review, and publish PRIIPs Key Information Documents
          </Typography>
        </Box>
      </Box>

      {/* Selectors and Generate */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Fund</InputLabel>
                <Select
                  value={selectedFundId}
                  onChange={(e) => setSelectedFundId(e.target.value)}
                  label="Fund"
                >
                  {funds.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" disabled={!selectedFundId}>
                <InputLabel>Share Class</InputLabel>
                <Select
                  value={selectedShareClassId}
                  onChange={(e) => setSelectedShareClassId(e.target.value)}
                  label="Share Class"
                >
                  {shareClasses.map((sc) => (
                    <MenuItem key={sc.id} value={sc.id}>
                      {sc.name} ({sc.isin || sc.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  label="Template"
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} /> : <Add />}
                onClick={handleGenerate}
                disabled={!selectedShareClassId || !selectedTemplateId || generating}
                fullWidth
              >
                {generating ? 'Generating...' : 'Generate KID'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* KID History */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Article sx={{ mr: 1, verticalAlign: 'middle' }} />
              Version History
            </Typography>
            {selectedShareClassId && (
              <IconButton size="small" onClick={() => {
                kidService.getHistory(selectedShareClassId).then(setKidHistory).catch((err) => {
                  LoggerService.error('Refresh KID history failed', err);
                });
              }}>
                <Refresh />
              </IconButton>
            )}
          </Box>

          {!selectedShareClassId ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Select a fund and share class to view KID documents
            </Typography>
          ) : kidHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No KID documents found for this share class. Generate one to get started.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>SRI</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Generated By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kidHistory.map((kid) => {
                    const chipInfo = STATUS_CHIP_MAP[kid.status] || { color: 'default' as const, label: kid.status };
                    return (
                      <TableRow key={kid.id} hover>
                        <TableCell>v{kid.version}</TableCell>
                        <TableCell>{new Date(kid.kid_date).toLocaleDateString()}</TableCell>
                        <TableCell>{renderSRIIndicator(kid.sri_value)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={chipInfo.label} color={chipInfo.color} />
                        </TableCell>
                        <TableCell>{kid.generated_by}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetail(kid)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(kid.id)}>
                              <Download />
                            </IconButton>
                          </Tooltip>
                          {(kid.status === 'draft' || kid.status === 'pending_review') && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApprove(kid.id)}
                                disabled={actionLoading === kid.id}
                              >
                                {actionLoading === kid.id ? <CircularProgress size={16} /> : <CheckCircle />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {kid.status === 'approved' && (
                            <Tooltip title="Publish">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handlePublish(kid.id)}
                                disabled={actionLoading === kid.id}
                              >
                                {actionLoading === kid.id ? <CircularProgress size={16} /> : <Publish />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* KID Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            KID Document — v{selectedKid?.version}
          </Typography>
          <IconButton onClick={() => setDetailDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedKid && (
            <Box>
              {/* Section 1: Product Info */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>1. Product</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share Class ID: {selectedKid.share_class_id} | Date: {new Date(selectedKid.kid_date).toLocaleDateString()}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Section 2: Risk Indicator */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>2. Summary Risk Indicator (SRI)</Typography>
              {renderSRIIndicator(selectedKid.sri_value)}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Risk level: {selectedKid.sri_value} out of 7
                {selectedKid.sri_value <= 2 && ' — Lower risk'}
                {selectedKid.sri_value >= 3 && selectedKid.sri_value <= 5 && ' — Medium risk'}
                {selectedKid.sri_value >= 6 && ' — Higher risk'}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Section 3: Performance Scenarios */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>3. Performance Scenarios</Typography>
              {renderPerformanceTable(selectedKid.performance_scenarios)}
              <Divider sx={{ my: 2 }} />

              {/* Section 4: Costs */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>4. Cost Breakdown</Typography>
              {renderCostTable(selectedKid.cost_breakdown)}
              <Divider sx={{ my: 2 }} />

              {/* Section 5: Recommended Holding Period */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>5. Recommended Holding Period</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedKid.data_snapshot?.recommended_holding_period || 'As specified in fund prospectus'}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Section 6: How to Complain */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>6. How to Complain</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedKid.data_snapshot?.complaint_procedure || 'Contact the management company directly'}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Section 7: Other Relevant Information */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>7. Other Relevant Information</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedKid.data_snapshot?.other_info || 'Please refer to the fund prospectus for additional details'}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Section 8: Metadata */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>8. Document Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    size="small"
                    label={STATUS_CHIP_MAP[selectedKid.status]?.label || selectedKid.status}
                    color={STATUS_CHIP_MAP[selectedKid.status]?.color || 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Generated At</Typography>
                  <Typography variant="body2">{new Date(selectedKid.generated_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Generated By</Typography>
                  <Typography variant="body2">{selectedKid.generated_by}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Approved By</Typography>
                  <Typography variant="body2">{selectedKid.approved_by || 'Not yet approved'}</Typography>
                </Grid>
                {selectedKid.published_at && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Published At</Typography>
                    <Typography variant="body2">{new Date(selectedKid.published_at).toLocaleString()}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDownload(selectedKid!.id)} startIcon={<Download />}>
            Download
          </Button>
          {selectedKid && (selectedKid.status === 'draft' || selectedKid.status === 'pending_review') && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleApprove(selectedKid.id)}
              startIcon={<CheckCircle />}
              disabled={actionLoading === selectedKid.id}
            >
              Approve
            </Button>
          )}
          {selectedKid?.status === 'approved' && (
            <Button
              variant="contained"
              color="success"
              onClick={() => handlePublish(selectedKid.id)}
              startIcon={<Publish />}
              disabled={actionLoading === selectedKid.id}
            >
              Publish
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KIDManagementPage;
